import { useMsal } from '@azure/msal-react';
import { BellIcon, ChatBubbleOvalLeftIcon, ListBulletIcon, MicrophoneIcon, PaperAirplaneIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Badge, Box, Button, Group, Overlay, Paper, ScrollArea, Stack, Text, TextInput, Title, Transition, useMantineColorScheme } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useNavigate } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../auth/AuthProvider';
import { ROUTES } from '../../constants/routes';
import { useNotificationRefresh } from '../../context/NotificationRefreshContext';
import { usePersonFilter } from '../../context/PersonFilterContext';
import {
  NotificationType,
  type ParsedNotification,
  notificationService,
  useParsedNotifications
} from '../../features/Notifications';
import {
  getNotificationColor,
  getNotificationIcon,
  isUrgentNotification
} from '../../features/Notifications/utils/notificationMessages';
import { useColors } from '../../hooks/useColors';
import { useSpeech } from '../../hooks/useSpeech';
import { useTranslation } from '../../hooks/useTranslation';
import { ChatService } from '../../services/chatService';
import { PersonService } from '../../services/personService';
import { ChatType } from '../../types/chat';
import { PeopleAvailableSoonNotificationContent } from '../../types/notification';
import { normalizeChartData } from '../../utils/chartDataNormalizer';
import { MermaidChart } from './MermaidChart';
import { ThinkingLoader } from './ThinkingLoader';



interface Message {
  text: string;
  isUser: boolean;
  citations?: string;
  isValidation?: boolean;
}

interface FloatingChatProps {
  children?: React.ReactNode;
  onSendMessage?: (message: string) => void;
  initialMessages?: Message[];
  onMessagesChange?: (messages: Message[]) => void;
}

type ChatMode = 'chat' | 'notifications';

// Dynamic Daisy SVG Component
interface DaisySVGProps {
  hasNotifications: boolean;
  hasUrgentNotifications: boolean;
  isLoading: boolean;
  size?: number;
}

const DaisySVG = ({ hasNotifications, hasUrgentNotifications, isLoading, size = 32 }: DaisySVGProps) => {
  // Determine center colors based on notification state
  const getCenterColors = () => {
    if (hasUrgentNotifications) {
      return {
        outer: '#FF4444', // Red for urgent
        inner: '#CC0000'
      };
    } else if (hasNotifications) {
      return {
        outer: '#4A90E2', // Blue for regular notifications
        inner: '#2E5BBA'
      };
    } else {
      return {
        outer: '#FFD728', // Default yellow
        inner: '#FFBE23'
      };
    }
  };

  const colors = getCenterColors();
  const animationClass = hasNotifications ? (hasUrgentNotifications ? 'daisy-urgent' : 'daisy-notification') : '';
  const loadingClass = isLoading ? 'daisy-loading' : '';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      className={`${animationClass} ${loadingClass}`}
      style={{ transition: 'all 0.3s ease' }}
    >
      <path style={{ fill: "#F5EBDC" }} d="M462.716,141.263c-22.868,3.598-51.839,12.365-81.582,24.685s-56.428,26.606-75.143,40.232
        c-0.422,0.307-0.764,0.682-1.163,1.007c0.326-0.398,0.684-0.758,0.99-1.178c13.629-18.718,27.914-45.401,40.234-75.143
        c12.319-29.741,21.086-58.711,24.684-81.583c1.992-12.629-4.903-24.879-16.78-29.8c-11.861-4.913-25.398-1.124-32.915,9.215
        c-13.629,18.718-27.914,45.401-40.234,75.143c-12.319,29.741-21.086,58.711-24.684,81.583c-0.089,0.572-0.067,1.133-0.121,1.703
        c-0.054-0.569-0.032-1.132-0.121-1.705c-3.598-22.868-12.365-51.839-24.685-81.582s-26.606-56.428-40.232-75.143
        c-7.529-10.337-21.062-14.127-32.94-9.207c-11.855,4.91-18.743,17.16-16.758,29.793c3.598,22.868,12.365,51.839,24.685,81.582
        s26.606,56.428,40.232,75.143c0.341,0.468,0.755,0.851,1.119,1.291c-0.44-0.364-0.822-0.777-1.29-1.118
        c-18.718-13.629-45.401-27.914-75.143-40.234c-29.741-12.319-58.711-21.086-81.583-24.684c-12.617-1.986-24.873,4.896-29.8,16.78
        c-4.91,11.854-1.122,25.393,9.215,32.915c18.718,13.629,45.401,27.914,75.143,40.234c29.741,12.319,58.711,21.086,81.583,24.684
        c0.513,0.082,1.022,0.082,1.533,0.133c-0.513,0.052-1.02,0.029-1.535,0.109c-22.868,3.598-51.839,12.365-81.582,24.685
        s-56.428,26.606-75.143,40.232c-10.337,7.529-14.127,21.062-9.207,32.94c4.91,11.855,17.16,18.743,29.793,16.758
        c22.868-3.598,51.839-12.365,81.582-24.685s56.428-26.606,75.143-40.232c0.466-0.34,0.846-0.75,1.286-1.113
        c-0.362,0.439-0.774,0.819-1.112,1.284c-13.629,18.718-27.914,45.401-40.234,75.143c-12.319,29.741-21.086,58.711-24.684,81.583
        c-1.992,12.629,4.903,24.879,16.78,29.8c7.006,2.902,14.596,2.767,21.192,0.035c4.564-1.89,8.646-5.023,11.723-9.25
        c13.629-18.718,27.914-45.401,40.234-75.143c12.319-29.741,21.086-58.711,24.684-81.583c0.089-0.572,0.067-1.133,0.121-1.703
        c0.054,0.569,0.032,1.132,0.121,1.705c3.598,22.868,12.365,51.839,24.685,81.582c12.32,29.743,26.606,56.428,40.232,75.143
        c7.529,10.337,21.062,14.127,32.94,9.207c11.855-4.91,18.743-17.16,16.758-29.793c-3.598-22.868-12.365-51.839-24.685-81.582
        s-26.606-56.428-40.232-75.143c-0.341-0.468-0.755-0.851-1.119-1.291c0.44,0.364,0.822,0.777,1.29,1.118
        c18.718,13.629,45.401,27.914,75.143,40.234c29.741,12.319,58.711,21.086,81.583,24.684c5.162,0.815,10.263,0.143,14.827-1.747
        c6.594-2.731,12.064-8.011,14.973-15.033c4.91-11.854,1.122-25.393-9.215-32.915c-18.718-13.629-45.401-27.914-75.143-40.234
        c-29.741-12.319-58.711-21.086-81.583-24.684c-0.573-0.089-1.135-0.068-1.706-0.121c0.57-0.054,1.135-0.032,1.708-0.121
        c22.868-3.598,51.839-12.365,81.582-24.685s56.428-26.606,75.143-40.232c10.337-7.529,14.127-21.062,9.207-32.94
        C487.597,146.166,475.348,139.277,462.716,141.263z"/>
      <path style={{ fill: "#EBDED4" }} d="M391.267,190.415L272.31,239.689l49.274-118.959c1.866-4.505-0.271-9.666-4.777-11.532
        c-4.505-1.866-9.666,0.271-11.532,4.777L256,232.934l-49.273-118.957c-1.866-4.504-7.03-6.643-11.534-4.778
        c-4.504,1.866-6.643,7.03-4.778,11.533l49.273,118.957l-118.959-49.274c-4.505-1.866-9.666,0.271-11.532,4.777
        s0.271,9.666,4.777,11.532L232.934,256l-118.957,49.273c-4.504,1.866-6.643,7.03-4.778,11.534c1.866,4.504,7.03,6.643,11.533,4.778
        l118.957-49.273l-49.274,118.959c-1.866,4.505,0.271,9.666,4.777,11.532c2.253,0.934,4.67,0.865,6.756,0.001
        s3.843-2.525,4.777-4.778L256,279.066l49.273,118.957c1.866,4.504,7.03,6.643,11.534,4.778c4.504-1.866,6.643-7.03,4.778-11.534
        L272.312,272.31l118.959,49.274c2.253,0.934,4.67,0.865,6.756,0.001s3.843-2.525,4.777-4.778c1.866-4.505-0.271-9.666-4.777-11.532
        L279.066,256l118.957-49.273c4.504-1.866,6.643-7.03,4.778-11.534C400.936,190.689,395.772,188.55,391.267,190.415z"/>
      <path style={{ fill: "#FFF5DC" }} d="M490.888,229.103c-22.504-5.427-52.625-8.414-84.819-8.414
        s-62.315,2.987-84.819,8.414c-0.507,0.122-0.967,0.338-1.461,0.486c0.454-0.244,0.921-0.438,1.366-0.711
        c19.754-12.077,43.164-31.263,65.927-54.026s41.949-46.173,54.026-65.927c6.673-10.905,4.991-24.862-4.099-33.953
        c-9.078-9.078-23.034-10.759-33.935-4.082c-19.754,12.077-43.164,31.263-65.927,54.026s-41.949,46.173-54.026,65.927
        c-0.301,0.493-0.497,1.021-0.764,1.526c0.168-0.547,0.404-1.058,0.54-1.621c5.427-22.503,8.414-52.623,8.414-84.818
        s-2.987-62.315-8.414-84.819C279.896,8.681,268.845,0,255.987,0c-12.832,0-23.884,8.681-26.883,21.112
        c-5.427,22.504-8.414,52.625-8.414,84.819s2.987,62.315,8.414,84.819c0.136,0.563,0.372,1.075,0.54,1.621
        c-0.268-0.505-0.462-1.033-0.764-1.526c-12.077-19.754-31.263-43.164-54.026-65.927s-46.173-41.949-65.927-54.026
        c-10.897-6.664-24.853-4.995-33.953,4.099c-9.074,9.074-10.754,23.03-4.082,33.935c12.077,19.754,31.263,43.164,54.026,65.927
        s46.173,41.949,65.927,54.026c0.444,0.271,0.913,0.467,1.365,0.711c-0.494-0.149-0.953-0.364-1.46-0.486
        c-22.504-5.428-52.625-8.415-84.819-8.415s-62.315,2.987-84.819,8.414C8.681,232.104,0,243.155,0,256.013
        c0,12.832,8.681,23.884,21.112,26.883c22.504,5.427,52.625,8.414,84.819,8.414s62.315-2.987,84.819-8.414
        c0.561-0.136,1.069-0.37,1.613-0.537c-0.503,0.267-1.028,0.46-1.518,0.761c-19.754,12.077-43.164,31.263-65.927,54.026
        s-41.949,46.173-54.026,65.927c-6.673,10.905-4.991,24.862,4.099,33.953c5.362,5.362,12.427,8.142,19.564,8.142
        c4.94,0,9.909-1.332,14.37-4.061c19.754-12.077,43.164-31.263,65.927-54.026s41.949-46.173,54.026-65.927
        c0.301-0.493,0.497-1.021,0.764-1.526c-0.168,0.547-0.404,1.058-0.54,1.621c-5.426,22.504-8.413,52.625-8.413,84.819
        s2.987,62.315,8.414,84.819c3,12.431,14.051,21.112,26.91,21.112c12.832,0,23.884-8.681,26.883-21.112
        c5.427-22.504,8.414-52.625,8.414-84.819s-2.987-62.315-8.414-84.819c-0.136-0.563-0.372-1.075-0.54-1.621
        c0.268,0.505,0.462,1.033,0.764,1.526c12.077,19.754,31.263,43.164,54.026,65.927s46.173,41.949,65.927,54.026
        c4.457,2.729,9.427,4.061,14.367,4.061c7.138,0,14.211-2.784,19.586-8.16c9.074-9.074,10.754-23.03,4.082-33.935
        c-12.077-19.754-31.263-43.164-54.026-65.927s-46.173-41.949-65.927-54.026c-0.494-0.301-1.023-0.498-1.529-0.765
        c0.548,0.169,1.06,0.405,1.624,0.541c22.504,5.427,52.625,8.414,84.819,8.414s62.315-2.987,84.819-8.414
        c12.431-3,21.112-14.051,21.112-26.91C512,243.155,503.319,232.104,490.888,229.103z"/>
      <path style={{ fill: "#F0E4C8" }} d="M406.069,247.172H277.31l91.048-91.048c3.448-3.448,3.448-9.035,0-12.483
        c-3.448-3.448-9.035-3.448-12.483,0l-91.047,91.048V105.931c0-4.875-3.953-8.828-8.828-8.828s-8.828,3.953-8.828,8.828v128.758
        l-91.048-91.048c-3.448-3.448-9.035-3.448-12.483,0c-3.448,3.448-3.448,9.035,0,12.483l91.048,91.048H105.931
        c-4.875,0-8.828,3.953-8.828,8.828s3.953,8.828,8.828,8.828h128.758l-91.048,91.048c-3.448,3.448-3.448,9.035,0,12.483
        c1.725,1.725,3.982,2.586,6.241,2.586s4.518-0.862,6.241-2.586l91.049-91.049v128.759c0,4.875,3.953,8.828,8.828,8.828
        s8.828-3.953,8.828-8.828V277.31l91.048,91.048c1.725,1.725,3.982,2.586,6.241,2.586c2.259,0,4.517-0.862,6.241-2.586
        c3.448-3.448,3.448-9.035,0-12.483l-91.048-91.047h128.759c4.875,0,8.828-3.953,8.828-8.828S410.944,247.172,406.069,247.172z"/>
      {/* Dynamic center circles */}
      <circle style={{ fill: colors.outer }} cx="256" cy="256" r="70.621" />
      <circle style={{ fill: colors.inner }} cx="256" cy="256" r="44.138" />
    </svg>
  );
};

const PREDEFINED_PROMPTS = [
  {
    category: "CV Creation",
    prompts: [
      "How should I structure my CV?",
      "What are the most important sections in a CV?",
      "How can I make my CV stand out?",
      "What should I include in my professional summary?"
    ],
    chatType: ChatType.General
  },
  {
    category: "Skills & Experience",
    prompts: [
      "How should I list my technical skills?",
      "How can I quantify my work experience?",
      "What's the best way to describe my achievements?",
      "How should I format my work history?"
    ],
    chatType: ChatType.General
  },
  {
    category: "Industry Specific",
    prompts: [
      "What skills are most valued in IT?",
      "How should I present my project experience?",
      "What certifications should I highlight?",
      "How to showcase my technical expertise?"
    ],
    chatType: ChatType.General
  },
  {
    category: "Team building",
    prompts: [
      "Who has azure certificate?",
      "List people with C# experience"
    ],
    chatType: ChatType.AllCvs
  }
];

// Individual prompt item component to handle hover state properly
interface PromptItemProps {
  prompt: string;
  onClick: () => void;
  colors: any;
}

const PromptItem = ({ prompt, onClick, colors }: PromptItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  return (
    <Paper
      p="xs"
      style={{
        backgroundColor: isHovered
          ? (isDark ? 'var(--mantine-color-dark-5)' : 'var(--mantine-color-gray-2)')
          : (isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)'),
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <Text size="sm">{prompt}</Text>
    </Paper>
  );
};

export function FloatingChat({
  children,
  onSendMessage,
  initialMessages = [],
  onMessagesChange
}: FloatingChatProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistories, setChatHistories] = useState<{ [key in ChatType]: Message[] }>({
    [ChatType.General]: [],
    [ChatType.AllCvs]: [],
  });
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [error, setError] = useState<string | null>(null);
  const [chatType, setChatType] = useState(ChatType.General);
  const [chatMode, setChatMode] = useState<ChatMode>('chat');
  const { currentLanguage } = useTranslation();
  useMsal(); // Keep for potential future use
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const colors = useColors();
  const navigate = useNavigate();
  const speakTextRef = useRef<(text: string) => void>(() => { });



  // Notifications hook
  const {
    notifications,
    messages: notificationMessages,
    loading: notificationsLoading,
    unreadCount,
    urgentCount,
    markAsRead,
    fetchNotifications,
    refreshUnreadCount
  } = useParsedNotifications();

  // Get auth state without excessive logging
  const { currentUser, isAuthenticated, isManager, isAdmin } = useAuth();

  // Notification refresh context
  const { registerRefreshCallback } = useNotificationRefresh();

  // Person filter context
  const { setFilteredPersons, setFilterTitle } = usePersonFilter();

  // Person service instance
  const personService = new PersonService();



  // Memoize the message change handler
  const handleMessagesChange = useCallback((newMessages: Message[]) => {
    if (onMessagesChange) {
      onMessagesChange(newMessages);
    }
  }, [onMessagesChange]);

  // Update messages when initialMessages change
  useEffect(() => {
    if (initialMessages.length > 0 && JSON.stringify(initialMessages) !== JSON.stringify(messages)) {
      setMessages(initialMessages);
    }
  }, [initialMessages]);

  // Register notification refresh callback
  useEffect(() => {
    const refreshAll = async () => {
      await Promise.all([
        fetchNotifications(),
        refreshUnreadCount()
      ]);
    };

    const unregister = registerRefreshCallback(refreshAll);
    return unregister;
  }, [registerRefreshCallback, fetchNotifications, refreshUnreadCount]);

  // Auto-switch to notifications tab when there are unread notifications
  useEffect(() => {
    if (unreadCount > 0 && chatMode === 'chat' && !isExpanded) {
      setChatMode('notifications');
    }
  }, [unreadCount, chatMode, isExpanded]);

  // Notify parent of message changes
  useEffect(() => {
    handleMessagesChange(messages);
  }, [messages, handleMessagesChange]);

  // Function to scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (isExpanded) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Scroll to bottom when chat is expanded
  useEffect(() => {
    if (isExpanded) {
      // Add a small delay to ensure the chat is fully expanded
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, scrollToBottom]);

  // When chatType changes, update messages to the correct history
  useEffect(() => {
    setMessages(chatHistories[chatType] || []);
  }, [chatType]);

  // Check if user has manager/admin access
  const hasManagerAccess = isManager || isAdmin;

  // When messages change, update the current chatType's history
  useEffect(() => {
    setChatHistories(prev => ({
      ...prev,
      [chatType]: messages,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);



  // Function to send message with specific text (with streaming)
  const handleSendWithText = useCallback(async (textToSend: string) => {
    if (textToSend.trim()) {
      setIsLoading(true);
      setError(null);
      const userMessage: Message = { text: textToSend, isUser: true };
      setMessages(prev => [...prev, userMessage]);

      // Add a placeholder message for the AI response
      const aiMessageIndex = messages.length + 1; // +1 for the user message we just added
      let accumulatedResponse = '';
      let accumulatedCitations = '';

      try {
        const chatService = ChatService.getInstance();

        // Stream the response
        for await (const chunk of chatService.streamChatMessage(textToSend, chatType)) {
          if (chunk.response) {
            accumulatedResponse += chunk.response;

            // Update the AI message with accumulated text
            setMessages(prev => {
              const newMessages = [...prev];
              const aiMsgIndex = newMessages.findIndex((msg, idx) => idx === aiMessageIndex && !msg.isUser);

              if (aiMsgIndex !== -1) {
                // Update existing AI message
                newMessages[aiMsgIndex] = {
                  text: accumulatedResponse,
                  isUser: false,
                  citations: accumulatedCitations
                };
              } else {
                // Add new AI message
                newMessages.push({
                  text: accumulatedResponse,
                  isUser: false,
                  citations: accumulatedCitations
                });
              }
              return newMessages;
            });
          }

          // Accumulate citations (usually comes in last chunk)
          if (chunk.citations) {
            accumulatedCitations = chunk.citations;

            // Update the message with citations
            setMessages(prev => {
              const newMessages = [...prev];
              const aiMsgIndex = newMessages.findIndex((msg, idx) => idx === aiMessageIndex && !msg.isUser);

              if (aiMsgIndex !== -1) {
                newMessages[aiMsgIndex] = {
                  ...newMessages[aiMsgIndex],
                  citations: accumulatedCitations
                };
              }
              return newMessages;
            });
          }
        }

        // Speak the complete response after streaming is done
        if (accumulatedResponse) {
          speakTextRef.current(accumulatedResponse);
        }

        if (onSendMessage) {
          onSendMessage(textToSend);
        }
        setMessage('');
      } catch (err) {
        console.error('[FloatingChat] Error in streaming chat:', err);
        setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
        const errorMessage: Message = {
          text: 'Sorry, I encountered an error while processing your message. Please try again.',
          isUser: false
        };

        // Replace or add error message
        setMessages(prev => {
          const newMessages = [...prev];
          const aiMsgIndex = newMessages.findIndex((msg, idx) => idx === aiMessageIndex && !msg.isUser);

          if (aiMsgIndex !== -1) {
            newMessages[aiMsgIndex] = errorMessage;
          } else {
            newMessages.push(errorMessage);
          }
          return newMessages;
        });

        // Speak the error message
        speakTextRef.current(errorMessage.text);
      } finally {
        setIsLoading(false);
      }
    }
  }, [chatType, onSendMessage, messages.length]);

  // Speech functionality
  const {
    isRecording,
    ttsEnabled,
    speakTextStreaming,
    handleMicrophoneClick,
    toggleTTS,
    stopSpeech,
    isSpeechAvailable
  } = useSpeech({
    autoSendOnRecognition: true,
    onAutoSend: handleSendWithText,
    onSetMessage: setMessage
  });

  // Update the ref with the speech function
  useEffect(() => {
    speakTextRef.current = speakTextStreaming;
  }, [speakTextStreaming, currentLanguage]);

  // Note: Speech recognition functionality has been removed to focus on TTS

  const handleSend = async () => {
    if (message.trim()) {
      await handleSendWithText(message);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };



  const formatCitations = (citations: string) => {
    return citations.split('\n').filter(line => line.trim());
  };

  const renderMessage = (msg: Message, index: number) => {
    // Show thinking loader for empty AI messages
    if (!msg.isUser && !msg.text) {
      return (
        <Box
          key={index}
          style={{
            marginBottom: 4,
            alignSelf: 'flex-start',
            borderRadius: '12px',
            borderBottomLeftRadius: '4px',
            padding: '8px 12px',
            fontSize: 'var(--mantine-font-size-sm)',
          }}
        >
          <ThinkingLoader />
        </Box>
      );
    }

    // Check if the message contains xychart format and wrap it in a mermaid code block
    let processedText = msg.text;
    if (!msg.isUser && (msg.text.includes('xychart-beta') || msg.text.includes('xychart'))) {
      // Extract the chart content and wrap it in a mermaid code block
      // Look for the entire chart content from xychart to the end or next double newline
      const chartMatch = msg.text.match(/(xychart-beta|xychart)[\s\S]*?(?=\n\n|$)/);
      if (chartMatch) {
        const chartContent = chartMatch[0].trim();

        // Normalize the chart content to fix newline issues
        const normalizedChartContent = normalizeChartData(chartContent);

        // Check if the content is already wrapped in a mermaid code block
        if (!chartContent.includes('```mermaid')) {
          // Replace the chart content with a proper mermaid code block
          processedText = msg.text.replace(
            chartContent,
            `\n\`\`\`mermaid\n${normalizedChartContent}\n\`\`\`\n`
          );
        }
      } else {
        // Try alternative approach - look for the entire content
        if (msg.text.includes('xychart-beta') || msg.text.includes('xychart')) {
          // Normalize the entire text and wrap in mermaid
          const normalizedText = normalizeChartData(msg.text);

          // Debug logging
          console.log('Chart data processing (fallback):');
          console.log('Original:', JSON.stringify(msg.text));
          console.log('Normalized:', JSON.stringify(normalizedText));

          processedText = `\`\`\`mermaid\n${normalizedText}\n\`\`\``;
        }
      }

      // Additional check: if the text already contains mermaid code blocks, don't double-wrap
      if (processedText.includes('```mermaid') && processedText.includes('```')) {
        // The text is already properly formatted
      } else if (msg.text.includes('xychart-beta') || msg.text.includes('xychart')) {
        // Final fallback: ensure the entire message is wrapped in mermaid
        if (!processedText.startsWith('```mermaid')) {
          const normalizedText = normalizeChartData(msg.text);
          processedText = `\`\`\`mermaid\n${normalizedText}\n\`\`\``;
        }
      }
    }
    return (
      <Box
        key={index}
        style={{
          marginBottom: 4,
          alignSelf: msg.isUser ? 'flex-end' : 'flex-start',
          borderRadius: '12px',
          borderBottomRightRadius: msg.isUser ? '4px' : '12px',
          borderBottomLeftRadius: msg.isUser ? '12px' : '4px',
          padding: '2px 8px',
          fontSize: 'var(--mantine-font-size-sm)',
          lineHeight: 1.5,
        }}
      >

        <Box
          style={{
            color: msg.isValidation
              ? (msg.text.includes('success')
                ? isDark ? 'var(--mantine-color-green-4)' : 'var(--mantine-color-green-7)'
                : isDark ? 'var(--mantine-color-red-4)' : 'var(--mantine-color-red-7)')
              : undefined,
            margin: 0,
            padding: 0,
            '& p': { margin: 0 },
            '& ul, & ol': { margin: 0, paddingLeft: '0.75em' },
            '& li': { margin: 0 },
            '& code': {
              backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)',
              padding: '0.05em 0.2em',
              borderRadius: '3px',
              fontSize: '0.9em',
            },
            '& pre': {
              backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)',
              padding: '0.2em',
              borderRadius: '4px',
              overflowX: 'auto',
            },
            '& blockquote': {
              borderLeft: `3px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-4)'}`,
              margin: 0,
              paddingLeft: '0.5em',
              color: isDark ? 'var(--mantine-color-dark-2)' : 'var(--mantine-color-gray-7)',
            },
            '& a': {
              color: isDark ? 'var(--mantine-color-blue-4)' : 'var(--mantine-color-blue-7)',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            },

          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <>{children}</>,
              table: ({ children }) => (
                <div className="markdown-table-wrapper">
                  <table className="markdown-table">
                    {children}
                  </table>
                </div>
              ),
              code({ node, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                if (match && match[1] === 'mermaid') {
                  // Robustly extract the Mermaid code block
                  const chartContent = Array.isArray(children)
                    ? children.join('').trim()
                    : String(children).trim();
                  return <MermaidChart chart={chartContent} />;
                }
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              }
            }}
          >
            {processedText}
          </ReactMarkdown>
        </Box>
        {msg.citations && (
          <Stack gap={4} mt={5}>
            {formatCitations(msg.citations).map((citation, citationIndex) => (
              <Link
                key={citationIndex}
                to={`/person/${citation.split(':')[0]}`}
              >
                <Text
                  size="xs"
                  c="blue"
                  style={{ fontSize: '11px', '&:hover': { textDecoration: 'underline' } }}
                >
                  {citation.split(':')[1]}
                </Text>
              </Link>
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  const handleClearChat = () => {
    setMessages([]);
    ChatService.getInstance().clearChatHistory();
  };

  // Navigation handlers for notifications
  const handleNotificationClick = async (notification: ParsedNotification) => {
    // Handle special local notification for CV creation
    if (notification.id === -1) {
      console.log('Navigating to CV editor new for CV creation');
      navigate(ROUTES.CV_NEW);
      setIsExpanded(false);
      return;
    }

    // Mark as read when clicked
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    switch (notification.type) {
      case NotificationType.CertificationExpiringSoon:
      case NotificationType.CVNotUpdated:
      case NotificationType.OnBenchUpdateCV:
      case NotificationType.LowCVScore:
        // These notification types have personId in their content
        const personId = (notification.content as any).personId;
        if (personId) {
          navigate(ROUTES.CV_EDITOR_WITH_ID(personId));
        } else {
          console.error('No personId found in notification content:', notification);
        }
        break;
      case NotificationType.PeopleAvailableSoon:
        handlePeopleAvailableSoonClick(notification);
        break;
      default:
        console.log('Unknown notification type:', notification.type);
    }

    // Close the chat after navigation
    setIsExpanded(false);
  };

  // Handle people available soon notification click
  const handlePeopleAvailableSoonClick = async (notification: ParsedNotification) => {
    try {
      const content = notification.content as PeopleAvailableSoonNotificationContent;

      if (!content.people || content.people.length === 0) {
        console.error('No people found in PeopleAvailableSoon notification');
        return;
      }

      // Extract person IDs from the notification content
      const personIds = content.people.map(person => person.personId);

      if (personIds.length === 0) {
        console.error('No valid person IDs found in notification content');
        return;
      }

      console.log('Fetching persons with IDs:', personIds);

      // Fetch the full person data using the new endpoint
      const filteredPersons = await personService.getPersonsByIds({
        personIds,
        inactive: false, // Only show active people
        sortField: 'availabilityDate',
        sortDirection: 'Ascending'
      });

      console.log('Received filtered persons:', filteredPersons);

      // Set the filtered data in context
      setFilteredPersons(filteredPersons);
      setFilterTitle(`People Available Soon (${content.peopleCount} ${content.peopleCount === 1 ? 'person' : 'people'})`);

      // Navigate to the CV list page
      navigate(ROUTES.CV_LIST);

    } catch (error) {
      console.error('Error handling PeopleAvailableSoon notification:', error);

      // Show user-friendly error message
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }

      // Fallback: just navigate to CV list without filtering
      navigate(ROUTES.CV_LIST);
    }
  };

  const messageBackgroundColor = (isUser: boolean) => {
    if (isUser) {
      return colors.getChatColors().userMessage;
    } else {
      return colors.getChatColors().botMessage;
    }
  };

  // Render notification item
  const renderNotification = (notification: ParsedNotification, index: number) => {
    const message = notificationMessages[index];

    // Add safety check
    if (!message) {
      console.error('No message found for notification at index:', index, notification);
      return (
        <Paper
          key={notification.id}
          p="sm"
          style={{
            backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)',
            border: '2px solid red',
            borderRadius: '8px',
            marginBottom: '8px',
          }}
        >
          <Text size="sm" c="red">
            Error: Could not format notification message
          </Text>
          <Text size="xs" c="dimmed">
            Type: {notification.type}, ID: {notification.id}
          </Text>
        </Paper>
      );
    }

    const isUrgent = isUrgentNotification(notification);
    const icon = getNotificationIcon(notification.type);
    const severityColor = getNotificationColor(message.severity);

    return (
      <Paper
        key={notification.id}
        p="sm"
        style={{
          backgroundColor: notification.isRead
            ? (isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)')
            : (isDark ? 'var(--mantine-color-dark-6)' : 'white'),
          border: `2px solid ${notification.isRead ? 'transparent' : severityColor}`,
          borderRadius: '8px',
          marginBottom: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          opacity: notification.isRead ? 0.7 : 1,
          '&:hover': {
            backgroundColor: isDark ? 'var(--mantine-color-dark-5)' : 'var(--mantine-color-gray-1)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
        }}
        onClick={() => handleNotificationClick(notification)}
      >
        <Group gap="sm" align="flex-start">
          <Text size="lg" style={{ minWidth: '24px' }}>
            {icon}
          </Text>
          <Box style={{ flex: 1 }}>
            <Group justify="space-between" align="flex-start" mb="xs">
              <Text fw={600} size="sm" style={{ color: severityColor }}>
                {message.title}
              </Text>
              <Group gap="xs">
                {isUrgent && (
                  <Badge color="red" size="xs" variant="filled">
                    Urgent
                  </Badge>
                )}
                {!notification.isRead && (
                  <Badge color="blue" size="xs" variant="dot">
                    New
                  </Badge>
                )}
              </Group>
            </Group>
            <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
              {message.message}
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              {new Date(notification.createdAt).toLocaleDateString()} {new Date(notification.createdAt).toLocaleTimeString()}
            </Text>
          </Box>
        </Group>
      </Paper>
    );
  };

  // Handle mark all as read using the correct API endpoint
  const handleMarkAllAsRead = useCallback(async () => {
    if (!currentUser?.personId) {
      console.error('No person ID available for current user');
      return;
    }

    try {
      // Use the dedicated mark all as read endpoint
      await notificationService.markAllNotificationsAsRead(currentUser.personId);

      // Refresh notifications and unread count to update the UI
      await Promise.all([
        fetchNotifications(),
        refreshUnreadCount()
      ]);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [currentUser?.personId, fetchNotifications, refreshUnreadCount]);

  return (
    <>
      {isExpanded && <Overlay opacity={0.1} onClick={() => {
        // Stop TTS when closing the chat via overlay click
        stopSpeech();
        setIsExpanded(false);
      }} />}
      <Box
        style={{
          position: 'fixed',
          bottom: 20,
          left: 20,
          zIndex: 9999,
        }}
      >
        <Box style={{ position: 'relative' }}>
          <ActionIcon
            radius="xl"
            variant="filled"
            color="brand"
            onClick={() => {
              if (isExpanded) {
                // Stop TTS when closing the chat
                stopSpeech();
              }
              setIsExpanded(!isExpanded);
            }}
            style={{
              position: 'relative',
              zIndex: 10000,
              width: '56px',
              height: '56px',
              fontSize: '32px'
            }}
          >
            <DaisySVG
              hasNotifications={unreadCount > 0}
              hasUrgentNotifications={urgentCount > 0}
              isLoading={isLoading}
              size={32}
            />
          </ActionIcon>
          {unreadCount > 0 && (
            <Badge
              color={urgentCount > 0 ? 'red' : 'blue'}
              variant="filled"
              size="sm"
              style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                zIndex: 10001,
                minWidth: '20px',
                height: '20px',
                borderRadius: '10px',
                fontSize: '10px',
                fontWeight: 'bold',
                animation: urgentCount > 0 ? 'bounce 1s ease-in-out infinite' : 'none'
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Box>
        <Transition mounted={isExpanded} transition="slide-up" duration={200}>
          {(styles) => (
            <Paper
              shadow="xl"
              p="md"
              style={{
                ...styles,
                position: 'absolute',
                bottom: '70px',
                left: 0,
                width: showPrompts ? '700px' : '400px',
                height: '600px',
                backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                border: `2px solid ${colors.getSemanticColorValue('borderLight')}`,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: isDark
                  ? '0 8px 32px rgba(0, 0, 0, 0.35), 0 3px 8px rgba(0, 0, 0, 0.2)'
                  : '0 8px 32px rgba(0, 0, 0, 0.15), 0 3px 8px rgba(0, 0, 0, 0.1)',
                transition: 'width 0.2s ease-in-out',
              }}
            >
              <Stack style={{ flex: 1, overflow: 'hidden' }}>
                <Group justify="space-between" gap="sm" p="sm" style={{ borderBottom: `1px solid ${colors.getSemanticColorValue('borderLight')}`, paddingTop: '8px', paddingBottom: '8px' }}>
                  <Group gap="md" style={{ paddingLeft: '2px' }}>
                    <Box style={{ position: 'relative' }}>
                      <ActionIcon
                        variant={chatMode === 'notifications' ? 'filled' : 'subtle'}
                        color="blue"
                        onClick={() => setChatMode('notifications')}
                        title="Notifications"
                        size="lg"
                      >
                        <BellIcon width={20} height={20} />
                      </ActionIcon>
                      {unreadCount > 0 && (
                        <Badge
                          color="red"
                          variant="filled"
                          size="xs"
                          style={{
                            position: 'absolute',
                            top: '0px',
                            right: '0px',
                            minWidth: '16px',
                            height: '16px',
                            fontSize: '9px',
                            padding: 0,
                            zIndex: 10,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </Badge>
                      )}
                    </Box>
                    <ActionIcon
                      variant={chatMode === 'chat' && chatType === ChatType.General ? 'filled' : 'subtle'}
                      color="blue"
                      onClick={() => {
                        setChatMode('chat');
                        setChatType(ChatType.General);
                      }}
                      title="CV Assistant"
                      size="lg"
                    >
                      <ChatBubbleOvalLeftIcon width={20} height={20} />
                    </ActionIcon>
                    {hasManagerAccess && (
                      <ActionIcon
                        variant={chatMode === 'chat' && chatType === ChatType.AllCvs ? 'filled' : 'subtle'}
                        color="blue"
                        onClick={() => {
                          setChatMode('chat');
                          setChatType(ChatType.AllCvs);
                        }}
                        title="Manager Assistant"
                        size="lg"
                      >
                        <UserGroupIcon width={20} height={20} />
                      </ActionIcon>
                    )}
                  </Group>

                  <Group gap="xs">
                    {chatMode === 'chat' && (
                      <>
                        <ActionIcon
                          variant={ttsEnabled ? "filled" : "subtle"}
                          color={ttsEnabled ? "green" : "gray"}
                          onClick={toggleTTS}
                          disabled={!isSpeechAvailable()}
                          title={
                            !isSpeechAvailable()
                              ? "Text to speech not available"
                              : ttsEnabled
                                ? "Disable text to speech"
                                : "Enable text to speech"
                          }
                          size="lg"
                        >
                          🔊
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => setShowPrompts(!showPrompts)}
                          title={showPrompts ? "Hide prompts palette" : "Show prompts palette"}
                          size="lg"
                        >
                          <ListBulletIcon width={20} height={20} />
                        </ActionIcon>
                      </>
                    )}

                    {chatMode === 'notifications' && notifications.filter(n => !n.isRead).length > 0 && (
                      <Button
                        variant="subtle"
                        color="blue"
                        size="sm"
                        onClick={handleMarkAllAsRead}
                      >
                        Mark All Read
                      </Button>
                    )}

                    {chatMode === 'chat' && messages.length > 0 && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={handleClearChat}
                        title="Clear chat"
                        size="lg"
                      >
                        <TrashIcon width={20} height={20} />
                      </ActionIcon>
                    )}
                  </Group>
                </Group>
                <Box style={{ display: 'flex', flex: 1, gap: '16px', overflow: 'hidden' }}>
                  <Stack style={{ flex: 1, overflow: 'hidden' }}>
                    {chatMode === 'chat' ? (
                      <>
                        {messages.length === 0 && (
                          <Box
                            style={{
                              backgroundColor: colors.getAdaptiveColor(
                                colors.getSemanticColorValue('backgroundMedium'),
                                colors.getSemanticColorValue('backgroundDark')
                              ),
                              padding: '12px',
                              borderRadius: '8px',
                              marginBottom: '4px',
                            }}
                          >
                            <Text size="sm" fw={500} c={isDark ? 'blue.4' : 'dimmed'} mb={4}>
                              {chatType === ChatType.General ? 'CV Assistant' : 'Manager Assistant'}
                            </Text>
                            <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                              {chatType === ChatType.General
                                ? "Welcome! I'm here to help you with your CV. You can ask me questions about creating, editing, or searching for CVs. Feel free to inquire about specific skills, technologies, or general CV guidance."
                                : "Welcome! I'm your management assistant. I can help you find team members with specific skills, certifications, or experience. Ask me about team composition, skill distribution, or search for people with particular qualifications."}
                            </Text>
                          </Box>
                        )}
                        <ScrollArea
                          viewportRef={scrollAreaRef}
                          style={{ flex: 1 }}
                          type="hover"
                          offsetScrollbars
                          scrollbarSize={8}
                        >
                          <Stack gap="xs">
                            {messages.map((msg, index) => (
                              <Box
                                key={index}
                                style={{
                                  backgroundColor: messageBackgroundColor(msg.isUser),
                                  border: `2px solid ${colors.getSemanticColorValue('borderLight')}`,
                                  borderRadius: '8px',
                                  padding: '12px',
                                  marginBottom: '8px'
                                }}
                              >
                                {renderMessage(msg, index)}
                              </Box>
                            ))}
                          </Stack>
                        </ScrollArea>
                        {error && (
                          <Text color="red" size="sm" ta="center">{error}</Text>
                        )}
                        {isLoading && (
                          <Text size="sm" c="dimmed" ta="center">AI is typing...</Text>
                        )}
                        <Group mt="md">
                          <TextInput
                            placeholder="Type a message..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleKeyPress}
                            style={{ flex: 1 }}
                            disabled={isLoading}
                          />
                          <ActionIcon
                            onClick={handleMicrophoneClick}
                            disabled={isLoading || !isSpeechAvailable()}
                            variant={isRecording ? "filled" : "subtle"}
                            color={isRecording ? "red" : "blue"}
                            size="lg"
                            title={
                              !isSpeechAvailable()
                                ? "Voice recognition not available"
                                : isRecording
                                  ? "Stop recording"
                                  : "Start voice recording"
                            }
                          >
                            <MicrophoneIcon width={16} />
                          </ActionIcon>
                          <Button
                            onClick={handleSend}
                            disabled={!message.trim() || isLoading}
                            variant="filled"
                            size="sm"
                            loading={isLoading}
                          >
                            <PaperAirplaneIcon width={16} />
                          </Button>
                        </Group>
                      </>
                    ) : (
                      <>
                        {/* Notifications Mode */}
                        <Box
                          style={{
                            backgroundColor: colors.getAdaptiveColor(
                              colors.getSemanticColorValue('backgroundMedium'),
                              colors.getSemanticColorValue('backgroundDark')
                            ),
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '4px',
                          }}
                        >
                          <Group justify="space-between" align="center">
                            <Text size="sm" fw={500} c={isDark ? 'blue.4' : 'dimmed'}>
                              Notifications
                            </Text>
                            <Group gap="xs">
                              {unreadCount > 0 && (
                                <Badge color="blue" variant="light" size="sm">
                                  {unreadCount} unread
                                </Badge>
                              )}
                              {urgentCount > 0 && (
                                <Badge color="red" variant="filled" size="sm">
                                  {urgentCount} urgent
                                </Badge>
                              )}
                            </Group>
                          </Group>
                        </Box>

                        <ScrollArea
                          style={{ flex: 1 }}
                          type="hover"
                          offsetScrollbars
                          scrollbarSize={8}
                        >
                          {notificationsLoading ? (
                            <Text size="sm" c="dimmed" ta="center" mt="md">
                              Loading notifications...
                            </Text>
                          ) : notifications.length === 0 ? (
                            <Box ta="center" mt="md">
                              <Text size="sm" c="dimmed">
                                No notifications
                              </Text>
                              <Text size="xs" c="dimmed" mt="xs">
                                You're all caught up! 🎉
                              </Text>
                            </Box>
                          ) : (
                            <Stack gap="xs">
                              {notifications.map((notification, index) => {
                                return renderNotification(notification, index);
                              })}
                            </Stack>
                          )}
                        </ScrollArea>
                      </>
                    )}
                  </Stack>
                  <Transition mounted={showPrompts && chatMode === 'chat'} transition="slide-left" duration={200}>
                    {(styles) => (
                      <Paper
                        shadow="sm"
                        p="md"
                        style={{
                          ...styles,
                          width: '280px',
                          backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-0)',
                          border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                          borderRadius: '8px',
                          overflow: 'hidden',
                        }}
                      >
                        <Title order={4} mb="md" c={isDark ? 'white' : 'dark'}>Suggested Prompts</Title>
                        <ScrollArea style={{ height: 'calc(100% - 40px)' }}>
                          <Stack gap="md">
                            {PREDEFINED_PROMPTS.filter((category) => category.chatType === chatType).map((category: { category: string; prompts: string[]; chatType: ChatType }, index: number) => (
                              <Box key={index}>
                                <Text fw={500} mb="xs" c={isDark ? 'blue.4' : 'blue.7'}>
                                  {category.category}
                                </Text>
                                <Stack gap="xs">
                                  {category.prompts.map((prompt: string, promptIndex: number) => (
                                    <PromptItem
                                      key={promptIndex}
                                      prompt={prompt}
                                      onClick={() => setMessage(prompt)}
                                      colors={colors}
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </ScrollArea>
                      </Paper>
                    )}
                  </Transition>
                </Box>
              </Stack>
            </Paper>
          )}
        </Transition>
      </Box>
    </>
  );
}

// Add this at the end of the file, before the last closing brace
const keyframes = `
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-3px);
  }
  60% {
    transform: translateY(-2px);
  }
}

@keyframes centerColorPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes centerColorFlash {
  0%, 100% {
    opacity: 1;
  }
  25% {
    opacity: 0.3;
  }
  75% {
    opacity: 1;
  }
}

.daisy-loading {
  animation: spin 2s linear infinite;
}

.daisy-notification {
  animation: pulse 2s ease-in-out infinite;
}

.daisy-notification circle:last-child {
  animation: centerColorPulse 1.5s ease-in-out infinite;
}

.daisy-urgent {
  animation: bounce 1s ease-in-out infinite;
}

.daisy-urgent circle:last-child {
  animation: centerColorFlash 0.8s ease-in-out infinite;
}
`;

// Add style tag to inject keyframes
const style = document.createElement('style');
style.textContent = keyframes;
document.head.appendChild(style); 
